export const SUPPORTED_LOCALES = ["es-MX", "en-US"] as const

export type Locale = (typeof SUPPORTED_LOCALES)[number]

// The locales the storefront actually serves. Anything else (an /en URL, a remembered
// cookie, the app's device language) resolves to the default.
export const LOCALES: readonly Locale[] = ["es-MX"]

export const HAS_LANGUAGE_CHOICE = LOCALES.length > 1

export const DEFAULT_LOCALE: Locale = "es-MX"

export const LOCALE_COOKIE = "NEXT_LOCALE"

// The default locale takes no prefix, so every es-MX URL already shared or indexed still resolves.
export const LOCALE_PREFIXES: Partial<Record<Locale, string>> = { "en-US": "/en" }

const resolveAmong =
  (candidates: readonly Locale[]) =>
  (raw: string | undefined | null): Locale =>
    candidates.includes(raw as Locale) ? (raw as Locale) : DEFAULT_LOCALE

export const resolveLocale = resolveAmong(LOCALES)

export const resolveSupportedLocale = resolveAmong(SUPPORTED_LOCALES)

// Staff sections behind the admin gate keep every catalog so authors can check English copy.
const STAFF_SECTIONS = ["studio", "showcase"]

/** @param path - without a locale prefix, with or without a region, e.g. /studio/home or /mx/studio/home */
export const isStaffPath = (path: string): boolean => {
  const [first = "", second = ""] = path.split("/").slice(1)

  // The default region is served without its /<cc> segment, so the section may lead the path.
  // Inline rather than isRegionCode: next.config.ts imports this file without the @/ alias.
  return STAFF_SECTIONS.includes(/^[a-z]{2}$/.test(first) ? second : first)
}

export const servedLocales = (path: string): readonly Locale[] => (isStaffPath(path) ? SUPPORTED_LOCALES : LOCALES)

export const resolveServedLocale = (path: string, raw: string | undefined | null): Locale => resolveAmong(servedLocales(path))(raw)
